// Opts Zod out of its `new Function` probe, which a host page's CSP reports as
// a violation. Kept in the graph from here; the ordering guarantee comes from
// the `zod` chunk group in vite.config.ts, not from this import's position.
import '~/modules/zod'
import { defineCustomElement } from 'vue'
import type { App } from 'vue'
import { createPinia } from 'pinia'
import { I18nInjectionKey } from 'vue-i18n'
import { i18n } from 'i18n'
import { VolverPlugin } from '@volverjs/ui-vue'
import { normal } from '@volverjs/ui-vue/icons'
import custom from 'icons'
import { trackAppException } from '~/modules/applicationInsights'

// In lib mode the ceStripBaseStyles plugin removes the @use from each .ce.vue,
// so we import the design system CSS once here and pass it via `styles`.
// In dev mode the .ce.vue files already include the @use, so we skip it.
import baseStyles from '~/assets/scss/style.scss?inline'

// Import the Vue components.
import FloatingChatWidget from '~/components/FloatingChatWidget.ce.vue'
import EmbeddedChatWidget from '~/components/EmbeddedChatWidget.ce.vue'

// Turn the Vue components into Custom Element classes.
function configureApp(app: App) {
    const { t } = i18n.global
    app.use(createPinia())
    app.use(i18n)
    // Inside a custom element, vue-i18n's `useI18n()` injects `I18nInjectionKey`
    // instead of the app-scoped `__VUE_I18N_SYMBOL__` that `app.use(i18n)`
    // provides. Provide the instance under that key too so composition-API
    // `useI18n()` calls resolve within the widget's shadow tree.
    app.provide(I18nInjectionKey, i18n)
    app.use(VolverPlugin, {
        iconsCollections: [normal, custom],
        defaults: {
            VvCombobox: {
                noResultsLabel: t('message.noResults'),
                noOptionsLabel: t('message.noOptions'),
                selectedHintLabel: t('label.selectedHint'),
                deselectActionLabel: t('label.deselectAction'),
                selectHintLabel: t('action.selectHint'),
                deselectHintLabel: t('action.deselectHint'),
                searchPlaceholder: t('placeholder.search'),
                closeLabel: t('action.close'),
                strategy: 'fixed',
                autoPlacement: {
                    allowedPlacements: ['top', 'bottom'],
                },
                searchable: true,
            },
        },
    })

    // Track Vue component errors via Application Insights.
    // initializeAppInsights() is called lazily from each widget component,
    // so trackAppException safely no-ops until the SDK is ready.
    app.config.errorHandler = (error, _instance, info) => {
        const normalizedError =
            error instanceof Error ? error : new Error(String(error))
        trackAppException(normalizedError, { vueInfo: info })
    }
}

function getCeOptions(component: unknown) {
    const { styles } = component as { styles?: string[] }
    return {
        configureApp,
        ...(import.meta.env.DEV
            ? {}
            : { styles: [baseStyles, ...(styles ?? [])] }),
    }
}

export const FloatingChatWidgetCe = defineCustomElement(
    FloatingChatWidget,
    getCeOptions(FloatingChatWidget),
)

export const EmbeddedChatWidgetCe = defineCustomElement(
    EmbeddedChatWidget,
    getCeOptions(EmbeddedChatWidget),
)

// Remember to register the element classes with the browser.
customElements.define('floating-chat-widget', FloatingChatWidgetCe)
customElements.define('embedded-chat-widget', EmbeddedChatWidgetCe)

// Add the new element types to Vue's GlobalComponents type.
declare module 'vue' {
    interface GlobalComponents {
        'floating-chat-widget': typeof FloatingChatWidget
        'embedded-chat-widget': typeof EmbeddedChatWidget
    }
}

// Add the element types to the global HTMLElementTagNameMap for vanilla TS consumers.
export interface FloatingChatWidgetElement extends HTMLElement {
    organizationSlug: string
    agentId: string
    baseUrl?: string
    externalToken?: string
    awaitExternalToken?: boolean
    /** Host-session mode: use the host platform's better-auth session from the cookie (no widget sign-in). */
    useHostSession?: boolean
    floatingButtonPosition?: 'left' | 'right'
    customCss?: string
    appinsightsConnectionString?: string
    open(): void
    close(): void
    toggle(): void
    sendMessage(text: string): void
    startNewChat(): void
    setContext(context: Record<string, unknown>): void
    /** Authenticates with an external JWT (same flow as the `external-token` attribute). */
    signIn(externalToken: string): Promise<void>
    /** Signs out and withdraws the active external token (no automatic re-login with it). */
    signOut(): Promise<void>
    readonly isOpen: boolean
}

export interface EmbeddedChatWidgetElement extends HTMLElement {
    organizationSlug: string
    agentId: string
    baseUrl?: string
    externalToken?: string
    awaitExternalToken?: boolean
    /** Host-session mode: use the host platform's better-auth session from the cookie (no widget sign-in). */
    useHostSession?: boolean
    /** Layout mode: `widget` (default), `fullscreen` (full-page app layout) or `panel` (compact chrome for a docked side panel; the host owns background, border and open/close). */
    displayMode?: 'widget' | 'fullscreen' | 'panel'
    forwardedSlots?: string
    customCss?: string
    appinsightsConnectionString?: string
    sendMessage(text: string): void
    startNewChat(): void
    setContext(context: Record<string, unknown>): void
    /** Authenticates with an external JWT (same flow as the `external-token` attribute). */
    signIn(externalToken: string): Promise<void>
    /** Signs out and withdraws the active external token (no automatic re-login with it). */
    signOut(): Promise<void>
}

declare global {
    interface HTMLElementTagNameMap {
        'floating-chat-widget': FloatingChatWidgetElement
        'embedded-chat-widget': EmbeddedChatWidgetElement
    }
}
